import { z } from 'zod';
import { ApiClient } from '../lib/api';
import { Photo, PhotoSchema } from '../models/photo';

// Define the response schemas
const ApiPhotoSchema = PhotoSchema.extend({}).omit({});
const ApiPhotoResponseSchema = ApiPhotoSchema.array();

const ApiPhotosResponseSchema = ApiPhotoSchema.array();

export type PhotoResponse = z.infer<typeof ApiPhotoResponseSchema>;

export type PhotosResponse = z.infer<typeof ApiPhotosResponseSchema>;

export class PhotoService {
  private readonly api: ApiClient;

  constructor(baseURL: string, stage: string) {
    this.api = new ApiClient(`${baseURL}/${stage}/photos-service-${stage}`, stage);
  }

  /**
   * Upload a new photo
   * @param photos The photo object to upload
   * @returns The created photo object
   */
  async createPhotos(photos: Partial<Photo>[]): Promise<PhotoResponse> {
    const response = await this.api.request(
      {
        method: 'POST',
        url: '/photos?skipPhotoname=true',
        data: JSON.stringify(photos),
        headers: {
          'Content-Type': 'application/json',
        },
      },
      ApiPhotoResponseSchema
    );
    
    const photosResponse = response.map(photo => {   
      if (photo.photodts) {
        photo.photodts = new Date(photo.photodts as unknown as string);
      }
      return photo as Photo;
    });
    
    return photosResponse;
  }

  /**
   * Update existing photos
   * @param photos The photo objects to update
   * @returns The updated photo objects
   */
  async updatePhotos(photos: Partial<Photo>[]): Promise<number[]> {
    const response = await this.api.request(
      {
        method: 'PUT',
        url: '/photos',
        data: JSON.stringify(photos),
        headers: {
          'Content-Type': 'application/json',
        },
      },
      z.array(z.number())
    );
    
    return response;
  }

  /**
   * Get photos by inspection ID
   * @param inspectionId The inspection ID to get photos for
   * @returns A list of photos for the inspection
   */
  async getPhotosByInspectionId(inspectionId: number): Promise<PhotosResponse> {
    const response = await this.api.request(
      {
        method: 'GET',
        url: `/photos/${inspectionId}`,
        headers: {
          'Content-Type': 'application/json',
        },
      },
      ApiPhotosResponseSchema
    );
    
    // Convert dates to proper Date objects
    const photos = response.map(photo => {
      if (photo.photodts) {
        photo.photodts = new Date(photo.photodts as unknown as string);
      }
      return photo as Photo;
    });
    
    return photos;
  }

  /**
   * Get photos by job ID
   * @param jobId The job ID to get photos for
   * @returns A list of photos for the job
   */
  async getPhotosByJobId(jobId: number, excludeIds?: number[]): Promise<PhotosResponse> {
    const response = await this.api.request(
      {
        method: 'GET',
        url: `/photos/job/${jobId}?excludeIds=${excludeIds?.join(',')}`,
        headers: {
          'Content-Type': 'application/json',
        },
      },
      ApiPhotosResponseSchema
    );
    
    // Convert dates to proper Date objects
    const photos = response.map(photo => {
      if (photo.photodts) {
        photo.photodts = new Date(photo.photodts as unknown as string);
      }
      return photo as Photo;
    });
    
    return photos;
  }

  /**
   * Get photos by quote section tracking ID
   * @param sectionTrackingId The section tracking ID to get photos for
   * @returns A list of photos for the quote section
   */
  async getPhotosBySectionTrackingId(sectionTrackingId: string): Promise<PhotosResponse> {
    const response = await this.api.request(
      {
        method: 'GET',
        url: `/photos/section/${sectionTrackingId}`,
        headers: {
          'Content-Type': 'application/json',
        },
      },
      ApiPhotosResponseSchema
    );
    
    // Convert dates to proper Date objects
    const photos = response.map(photo => {
      if (photo.photodts) {
        photo.photodts = new Date(photo.photodts as unknown as string);
      }
      return photo as Photo;
    });
    
    return photos;
  }

  /**
   * Delete a photo
   * @param photoId The ID of the photo to delete
   * @returns Success message
   */
  async deletePhoto(photoId: number): Promise<{ message: string }> {
    return this.api.request(
      {
        method: 'DELETE',
        url: `/photos/delete/${photoId}`,
        headers: {
          'Content-Type': 'application/json',
        },
      },
      z.object({ message: z.string() })
    );
  }
}
